import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Github, Calendar, MapPin } from 'lucide-react';

const Contact = ({ data }) => {
    const contactLinks = [
        {
            icon: <Mail className="w-5 h-5" />,
            label: "Email",
            value: data.email,
            href: `mailto:${data.email}`,
            hoverColor: "hover:border-blue-500/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)]"
        },
        {
            icon: <Linkedin className="w-5 h-5" />,
            label: "LinkedIn",
            value: "linkedin.com/in/vcuerdo",
            href: data.website,
            hoverColor: "hover:border-[#0077B5]/50 hover:shadow-[0_0_30px_rgba(0,119,181,0.15)]"
        },
        {
            icon: <Github className="w-5 h-5" />,
            label: "GitHub",
            value: "VictorCuerdo",
            href: data.github,
            hoverColor: "hover:border-white/20 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)]"
        },
        {
            icon: <Calendar className="w-5 h-5" />,
            label: "Schedule a Call",
            value: "30-min meeting",
            href: data.calendly,
            hoverColor: "hover:border-violet-500/50 hover:shadow-[0_0_30px_rgba(139,92,246,0.15)]"
        }
    ];

    return (
        <section id="contact" className="py-24 md:py-32 bg-background-alt relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="max-w-4xl mx-auto px-6 relative z-10">
                {/* Section header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <span className="text-primary font-mono text-sm tracking-wider uppercase mb-3 block">
                        Get In Touch
                    </span>
                    <h2 className="text-3xl md:text-5xl font-display font-bold text-text tracking-tight mb-4">
                        Let's Connect
                    </h2>
                    <p className="text-text-secondary max-w-xl mx-auto text-lg">
                        {data.contactmessage}
                    </p>
                </motion.div>

                {/* Contact cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
                    {contactLinks.map((link, index) => (
                        <motion.a
                            key={index}
                            href={link.href}
                            target={link.href?.startsWith('mailto') ? undefined : '_blank'}
                            rel="noreferrer"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className={`group flex items-center gap-4 p-5 rounded-2xl bg-surface border border-border transition-all duration-500 ${link.hoverColor}`}
                        >
                            <div className="p-3 rounded-xl bg-surface-highlight text-text-secondary group-hover:text-primary transition-colors duration-300 border border-border">
                                {link.icon}
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-text group-hover:text-primary transition-colors">
                                    {link.label}
                                </h3>
                                <p className="text-text-tertiary text-xs mt-0.5">
                                    {link.value}
                                </p>
                            </div>
                        </motion.a>
                    ))}
                </div>

                {/* Location */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-center gap-2 text-text-tertiary text-sm"
                >
                    <MapPin size={14} />
                    <span>{data.address?.city}, {data.address?.country}</span>
                    <span className="mx-2 text-border-light">|</span>
                    <span>Remote-first, US timezone overlap</span>
                </motion.div>
            </div>
        </section>
    );
};

export default Contact;
